import { useQuery } from "@tanstack/react-query";
import { useState } from "react";
import { useSearchParams } from "react-router-dom";
import { apiClient, responseData } from "@/api/client";
import type { JsonObject } from "@/api/json";
import { Breadcrumb } from "@/components/Breadcrumb";
import { Empty, PageLead, Panel } from "@/components/Page";
import { Pagination } from "@/components/Pagination";

interface AuditEvent {
  id: string;
  action: string;
  objectType: string;
  objectId: string;
  message: string;
  data: JsonObject;
  createdAt: string;
  actor?: { username: string; displayName: string };
}

export function AuditPage() {
  const [params, setParams] = useSearchParams();
  const page = Number(params.get("page") ?? 1);
  const q = params.get("q") ?? "";
  const action = params.get("action") ?? "";
  const objectType = params.get("objectType") ?? "";
  const actor = params.get("actor") ?? "";
  const from = params.get("from") ?? "";
  const to = params.get("to") ?? "";
  const [draft, setDraft] = useState({
    q,
    action,
    objectType,
    actor,
    from,
    to,
  });
  const events = useQuery({
    queryKey: ["audit", page, q, action, objectType, actor, from, to],
    queryFn: (): Promise<{ items: AuditEvent[]; meta: { page: number; pageSize: number; total: number; totalPages: number; hasNextPage: boolean; hasPreviousPage: boolean } }> =>
      responseData(apiClient.GET("/api/v1/audit/events", {
        params: { query: { page, pageSize: 50, q, action, objectType, actor, from, to } },
      })),
  });
  const apply = () => {
    const next = new URLSearchParams();
    Object.entries(draft).forEach(
      ([key, value]) => value && next.set(key, value),
    );
    next.set("page", "1");
    setParams(next);
  };
  const setPage = (value: number) => {
    const next = new URLSearchParams(params);
    next.set("page", String(value));
    setParams(next);
  };
  return (
    <>
      <Breadcrumb items={[{ label: "Auditoría" }]} />
      <PageLead
        eyebrow="Trazabilidad"
        title="Auditoría"
        description="Logs del sistema de conciliaciones."
      />
      <Panel
        title="Línea de tiempo"
        meta={`${events.data?.meta.total ?? 0} eventos`}
      >
        <form
          className="audit-filters"
          onSubmit={(event) => {
            event.preventDefault();
            apply();
          }}
        >
          <label className="audit-filters__search">
            Buscar
            <input
              value={draft.q}
              onChange={(event) =>
                setDraft({ ...draft, q: event.target.value })
              }
              placeholder="Mensaje, acción o identificador"
            />
          </label>
          <label>
            Actor
            <input
              value={draft.actor}
              onChange={(event) =>
                setDraft({ ...draft, actor: event.target.value })
              }
            />
          </label>
          <label>
            Acción
            <input
              value={draft.action}
              onChange={(event) =>
                setDraft({ ...draft, action: event.target.value })
              }
            />
          </label>
          <label>
            Objeto
            <input
              value={draft.objectType}
              onChange={(event) =>
                setDraft({ ...draft, objectType: event.target.value })
              }
            />
          </label>
          <label>
            Desde
            <input
              type="date"
              value={draft.from}
              onChange={(event) =>
                setDraft({ ...draft, from: event.target.value })
              }
            />
          </label>
          <label>
            Hasta
            <input
              type="date"
              value={draft.to}
              onChange={(event) =>
                setDraft({ ...draft, to: event.target.value })
              }
            />
          </label>
          <button className="button button--primary">Aplicar filtros</button>
        </form>
        {events.error && (
          <div className="message message--error">{events.error.message}</div>
        )}
        <div className="audit-timeline">
          {events.data?.items.map((event) => (
            <article key={event.id}>
              <time>
                {new Date(event.createdAt).toLocaleString("es-AR", {
                  hourCycle: "h23",
                })}
              </time>
              <i />
              <div>
                <strong>{event.message}</strong>
                <span>
                  {event.actor?.displayName ?? "Sistema"} · {event.action} ·{" "}
                  {event.objectType} {event.objectId.slice(0, 8)}
                </span>
              </div>
            </article>
          ))}
        </div>
        {!events.isLoading && !events.data?.items.length && (
          <Empty>No hay eventos para los filtros elegidos.</Empty>
        )}
        <Pagination meta={events.data?.meta} onPageChange={setPage} />
      </Panel>
    </>
  );
}
